<template>
  <div class="p-4">
    <!-- Кнопка "Добавить рабочее место" -->
    <button
      v-can-write="'workshop_posts'"
      @click="openAddWorkshopPostUnitModal"
      class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
    >
      Добавить рабочее место
    </button>

    <!-- Состояние загрузки -->
    <div v-if="loading" class="flex justify-center items-center h-40">
      <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
    </div>

    <!-- Таблица рабочих мест -->
    <table v-else class="w-full border-collapse bg-white shadow-md">
      <thead class="bg-gray-100">
        <tr>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">#</th>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Название рабочего места</th>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Рабочий центр</th>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Рабочий стол (модуль)</th>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Действия</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(unit, index) in workshopPostUnits" :key="unit.id" class="border-b hover:bg-gray-50">
          <td class="px-4 py-2 text-sm text-gray-700">{{ index + 1 }}</td>
          <td class="px-4 py-2 text-sm text-gray-700">{{ unit.name }}</td>
          <td class="px-4 py-2 text-sm text-gray-700">{{ getWorkshopPostName(unit.workshop_post_id) || 'Не указан' }}</td>
          <td class="px-4 py-2 text-sm text-gray-700">{{ unit.module?.name || 'Не указан' }}</td>
          <td class="px-4 py-2">
            <button
              v-can-write="'workshop_posts'"
              @click="confirmDelete(unit.id)"
              class="px-2 py-1 text-sm font-medium text-red-600 bg-red-100 rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              Удалить
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Модальное окно для добавления/редактирования рабочего места -->
    <div v-if="addWorkshopPostUnitModalVisible" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div class="bg-white rounded-lg shadow-lg w-[90%] max-w-md p-6">
        <h2 class="text-lg font-bold mb-4">{{ isEditMode ? 'Редактировать рабочее место' : 'Добавить рабочее место' }}</h2>
        <form @submit.prevent="saveWorkshopPostUnit">
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">Название рабочего места</label>
            <input
              type="text"
              v-model="newWorkshopPostUnit.name"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Введите название рабочего места"
              required
            />
          </div>
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">Рабочий центр</label>
            <select
              v-model="newWorkshopPostUnit.workshop_post_id"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Выберите рабочий центр</option>
              <option v-for="post in workshopPosts" :key="post.id" :value="post.id">
                {{ post.name }}
              </option>
            </select>
          </div>
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">Рабочий стол (модуль)</label>
            <select
              v-model="newWorkshopPostUnit.module_id"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Выберите модуль</option>
              <option v-for="module in modules" :key="module.id" :value="module.id">
                {{ module.name }}
              </option>
            </select>
          </div>
          <div class="flex justify-end space-x-2">
            <button
              type="button"
              @click="closeAddWorkshopPostUnitModal"
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Отмена
            </button>
            <button
              type="submit"
              class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {{ isEditMode ? 'Сохранить' : 'Добавить' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Модальное окно подтверждения удаления -->
    <div v-if="isConfirmModalVisible" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div class="bg-white rounded-lg shadow-lg w-[90%] max-w-md p-6">
        <h2 class="text-lg font-bold mb-4">Подтвердите удаление</h2>
        <p class="text-sm text-gray-700 mb-4">Вы уверены, что хотите удалить это рабочее место?</p>
        <div class="flex justify-end space-x-2">
          <button
            @click="cancelDelete"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Отмена
          </button>
          <button
            @click="proceedDelete"
            class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            Удалить
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      workshopPostUnits: [], // Список рабочих мест
      workshopPosts: [], // Список рабочих центров
      modules: [], // Список модулей (столов) для привязки
      loading: true, // Флаг загрузки
      addWorkshopPostUnitModalVisible: false, // Флаг видимости модального окна добавления/редактирования
      isEditMode: false, // Флаг режима редактирования
      newWorkshopPostUnit: {
        id: null, // ID редактируемого рабочего места (если есть)
        name: '', // Название рабочего места
        workshop_post_id: '', // ID рабочего центра
        module_id: '', // ID модуля (стола), к которому привязан рабочий центр
      },
      isConfirmModalVisible: false, // Флаг видимости модального окна подтверждения
      workshopPostUnitIdToDelete: null, // ID рабочего места для удаления
    };
  },
  created() {
    this.getWorkshopPosts();
    this.getWorkshopPostUnits();
    this.getModules();
  },
  methods: {
    /**
     * Загружает список рабочих центров.
     */
    getWorkshopPosts() {
      axios
        .post('/api/workshopPost/getWorkshopPosts')
        .then((response) => {
          this.workshopPosts = response.data.data;
        })
        .catch((error) => {
          console.error('Ошибка при загрузке рабочих центров:', error);
          alert('Не удалось загрузить рабочие центры. Попробуйте позже.');
        });
    },

    /**
     * Загружает список рабочих мест.
     */
    getWorkshopPostUnits() {
      axios
        .post('/api/workshopPostUnit/getWorkshopPostUnits', { module_id: this.$route.params.id })
        .then((response) => {
          this.workshopPostUnits = response.data.data;
          this.loading = false;
        })
        .catch((error) => {
          console.error('Ошибка при загрузке рабочих мест:', error);
          alert('Не удалось загрузить рабочие места. Попробуйте позже.');
          this.loading = false;
        });
    },

    /**
     * Загружает список модулей (столов) для привязки рабочего центра.
     */
    getModules() {
      axios
        .post('/api/module/getModules')
        .then((response) => {
          this.modules = response.data.data || [];
        })
        .catch((error) => {
          console.error('Ошибка при загрузке модулей:', error);
          alert('Не удалось загрузить список модулей. Попробуйте позже.');
        });
    },

    /**
     * Возвращает название рабочего центра по его ID.
     */
    getWorkshopPostName(workshopPostId) {
      const workshopPost = this.workshopPosts.find((post) => post.id === workshopPostId);
      return workshopPost ? workshopPost.name : null;
    },

    /**
     * Показывает модальное окно добавления рабочего места.
     */
    openAddWorkshopPostUnitModal() {
      this.isEditMode = false; // Режим создания
      this.newWorkshopPostUnit = { id: null, name: '', workshop_post_id: '', module_id: '' }; // Очищаем данные
      this.addWorkshopPostUnitModalVisible = true;
    },

    /**
     * Показывает модальное окно редактирования рабочего места.
     */
    openEditWorkshopPostUnitModal(unit) {
      this.isEditMode = true; // Режим редактирования
      this.newWorkshopPostUnit = { ...unit }; // Копируем данные рабочего места
      this.addWorkshopPostUnitModalVisible = true;
    },

    /**
     * Сохраняет или обновляет рабочее место.
     */
    saveWorkshopPostUnit() {
      if (!this.newWorkshopPostUnit.name || !this.newWorkshopPostUnit.workshop_post_id || !this.newWorkshopPostUnit.module_id) {
        alert('Заполните все поля');
        return;
      }

      const url = this.isEditMode
        ? '/api/workshopPostUnit/createOrUpdateWorkshopPostUnit'
        : '/api/workshopPostUnit/createOrUpdateWorkshopPostUnit';

      axios
        .post(url, this.newWorkshopPostUnit)
        .then(() => {
          this.getWorkshopPostUnits(); // Обновляем список рабочих мест
          this.closeAddWorkshopPostUnitModal(); // Закрываем модальное окно
        })
        .catch((error) => {
          console.error('Ошибка при сохранении рабочего места:', error);
          alert('Не удалось сохранить рабочее место. Попробуйте позже.');
        });
    },

    /**
     * Закрывает модальное окно добавления/редактирования.
     */
    closeAddWorkshopPostUnitModal() {
      this.addWorkshopPostUnitModalVisible = false;
      this.newWorkshopPostUnit = { id: null, name: '', workshop_post_id: '', module_id: '' }; // Очищаем данные
    },

    /**
     * Подтверждает удаление рабочего места.
     */
    confirmDelete(id) {
      this.workshopPostUnitIdToDelete = id;
      this.isConfirmModalVisible = true;
    },

    /**
     * Отменяет удаление рабочего места.
     */
    cancelDelete() {
      this.isConfirmModalVisible = false;
      this.workshopPostUnitIdToDelete = null;
    },

    /**
     * Удаляет рабочее место.
     */
    proceedDelete() {
      if (!this.workshopPostUnitIdToDelete) {
        console.error('ID рабочего места не указан');
        return;
      }

      axios
        .post('/api/workshopPostUnit/deleteWorkshopPostUnit', { id: this.workshopPostUnitIdToDelete })
        .then(() => {
          this.getWorkshopPostUnits(); // Обновляем список рабочих мест
          this.cancelDelete(); // Закрываем модальное окно
        })
        .catch((error) => {
          console.error('Ошибка при удалении рабочего места:', error);
          alert('Не удалось удалить рабочее место. Попробуйте позже.');
        });
    },
  },
};
</script>
